import { ENDPOINT_PATH } from '../config';

import { authFetch } from '../utils/authFetch';
/**
 * Helper function to upload artist profile picture
 * @param {*} artistProfilePicture - Image file
 * @returns URL of the image file gotten by api/upload-image
 */
const uploadArtistProfilePicture = async (artistProfilePicture) => {
    if (!(artistProfilePicture instanceof File)) {
        console.error("Invalid file provided.");
        return null;
    }
    try {
        const formData = new FormData();
        formData.append('file', artistProfilePicture);
        formData.append('folder', 'artistProfilePics/');

        const response = await authFetch(ENDPOINT_PATH + '/api/upload-image', {
            method: 'POST',
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`Error uploading image: ${response.statusText}`);
        }

        // Read the response as plain text which is the URL
        const imageUrl = await response.text(); 
        return imageUrl;
    } catch (error) {
        console.error("Image upload failed:", error);
        return null;
    }
}

/**
 * @param {*} artistDocId - String of an artist-doc's id
 * @param {*} publicView - Boolean: if true, only returns approved artwork (for public profiles)
 */
const getArtistData = async(artistDocId, publicView = false) => {
    const publicViewParam = publicView ? '&publicView=true' : '';
    return fetch(ENDPOINT_PATH + `/api/get-artist-data?artistDocId=${artistDocId}${publicViewParam}`)
        .then(response => {
            if (!response.ok) {
                throw new Error(`HTTP error status ${response.status}`);
            }
            return response.json();
        })
        .catch (error => {
            console.error("Error fetching artist data: ", error);
            return null;
        });
}

/**
 * @param {*} firstName - String of artist first name
 * @param {*} lastName - String of artist last name
 */
const deleteArtist = async (firstName, lastName) => {

    fetch(ENDPOINT_PATH + `/api/del-artist`, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            firstName: firstName,
            lastName: lastName,
        }),
    })
    .then(response => response.json())
    .then(data => {
        console.log('Successfully deleted artist:', data);
    })
    .catch(e => { console.error(e); })
};

/**
 * @param {*} id - User id for firestore db document
 * @param {*} update - An object that must contain a subset of fields of the exact name as the firestore db document. The exception is that if the profile picture of the artist 
 *                     is to change, the file must be passed in separately and named uploadedFile
 *                      - artistBio : ""
 *                      - email: ""
 *                      - firstName: ""
 *                      - lastName: ""
 *                      - phoneNumber: ""
 *                      - uploadedFile: File object
 */
const editArtist = async (id, update, uploadedFile) => {
    try {
        const formData = new FormData();
        formData.append('id', id);
        formData.append('update', JSON.stringify(update));
        if (uploadedFile) {
            formData.append('uploadedFile', uploadedFile);
        }

        const response = await fetch(ENDPOINT_PATH + `/api/edit-artist`, {
            method: 'PATCH',
            body: formData,
        });

        const data = await response.json();

        if (response.ok) {
            console.log('Successfully edited artist:', data);
            return data;
        } else {
            console.error('Error editing artist:', data);
            throw new Error(data.message || 'Failed to update artist profile');
        }
    } catch (error) {
        console.error('Error in editArtist:', error);
        throw error;
    }
};

/**
 * Upload artwork for an artist
 * @param {*} userId - String of the Firebase Auth user ID
 * @param {*} artworkName - String name of the artwork
 * @param {*} category - String category of the artwork
 * @param {*} price - Number or string price of the artwork
 * @param {*} artDescription - String description of the artwork (optional)
 * @param {*} artImage - File object of the artwork image
 * @returns Promise with response data
 */
const uploadArtistArtwork = async (userId, artworkName, category, _price, artDescription, artImage) => {
    if (!(artImage instanceof File)) {
        throw new Error("Invalid file provided. Please select an image file.");
    }

    try {
        // Create FormData to send file and other fields
        const formData = new FormData();
        formData.append('userId', userId);
        formData.append('artworkName', artworkName);
        formData.append('category', category);
        // Price is now admin-controlled; backend defaults to 0
        if (artDescription) {
            formData.append('artDescription', artDescription);
        }
        formData.append('artImage', artImage);

        const response = await fetch(ENDPOINT_PATH + '/api/artist/upload-artwork', {
            method: 'POST',
            body: formData, // Don't set Content-Type header - browser will set it with boundary
        });

        const data = await response.json();

        if (response.ok) {
            console.log("Artwork uploaded successfully:", data);
            return data;
        } else {
            console.error("Error uploading artwork:", data);
            throw new Error(data.message || 'Failed to upload artwork');
        }
    } catch (error) {
        console.error("Error in uploadArtistArtwork:", error);
        throw error;
    }
};

export { getArtistData, deleteArtist, editArtist, uploadArtistProfilePicture, uploadArtistArtwork };

